import React, {useContext, useState} from 'react'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputAdornment,
    Radio,
    RadioGroup, Stack,
    TextField,
    Theme,
    Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import CheckIcon from '@mui/icons-material/Check'
import {AppContext} from '../../contexts/AppContextProvider'
import ControlContainer from '../ControlContainer'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {GeneralComplaintState, MaterialResponse, ProductTypeOptions, ProductIdOptions, YesNoOptions,} from '../../contexts/types'
import {useForm} from '../../hooks/useForm'
import {resetClinicalCondition} from '../../contexts/ClinicalConditionReducer'
import IRSAlert from '../IRSAlert'
import axios from 'axios'
import http from '../../http'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {resetTreatmentDates, setTreatmentDates} from "../../contexts/TreatmentDatesReducer";
import {resetComplaintType} from "../../contexts/ComplaintTypeReducer";
import {setGeneralComplaint} from "../../contexts/GeneralComplaintReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            border: '1px solid #9D9D9D',
            backgroundColor: theme.palette.grey[200],
            padding: '10px 20px',
            color: '#656565',
        },
        buttons: {
            '& > *': {
                margin: theme.spacing(1),
            },
        },
        title: {
            display: 'flex',
            alignItems: 'center',
        },
        tableHeading: {
            fontWeight: 'bold',
        },
    })
)

const ProductType: React.FC = () => {
    const classes = useStyles()
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {t, orgIdentifier} = useContext(OrgCustomizationContext)
    const {
        complaint,
        dispatch,
        countryCode,
        customerNumber,
        postNotification,
        clearNotification
    } = useContext(AppContext)
    const [isValidatingBatchNum, setIsValidatingBatchNum] =
        useState<boolean>(false)
    const [isBatchNumValid, setIsBatchNumValid] = useState<boolean>(true)
    const [showBatchNumCheck, setShowBatchNumCheck] = useState<boolean>(false)
    const selectedProduct = complaint.generalComplaint.selectedProduct
    const required = {value: true, message: t('this_field_is_required')}
    const {
        data,
        errors,
        handleChangeEvent,
        validateFields,
        enableOnChangeValidation,
        setFormData,
    } = useForm<GeneralComplaintState>({
        initialValues: {
            ...complaint.generalComplaint,
            productType: selectedProduct?.productId === 'XUN001' ? 'IMPLANT' : complaint.generalComplaint.selectedProduct?.productType
        },
        validations: {
            isBatchOrLotNumberKnown: {required},
            wasProductPlaced: {required},
            isReplacementNeeded: {required},
            productType: {required},
            wasPatientInvolved: {
                custom: {
                    isValid: (value, data) => {
                        if (data.wasProductPlaced === 'NO') {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('this_field_is_required'),
                },
            },
            wasOpenedOrDropped: {
                custom: {
                    isValid: (value, data) => {
                        if (
                            data.productType === 'IMPLANT'
                            && (['us', 'ca', 'hk'].includes(countryCode))
                            && (['Nobel'].includes(orgIdentifier))
                        ) {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('this_field_is_required'),
                },
            },
            wasAnotherImplantPlaced: {
                custom: {
                    isValid: (value, data) => {
                        if (
                            data.productType === 'IMPLANT' &&
                            data.wasProductPlaced === 'YES'
                        ) {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('this_field_is_required'),
                },
            },
            batchOrLotNumber: {
                custom: {
                    isValid: (value, data) => {
                        if (data.isBatchOrLotNumberKnown === 'YES') {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('pt_valid_batch_or_lot_number_is_required'),
                },
            },
        },
    })

    const anotherImplantInitialValue: YesNoOptions = complaint.generalComplaint
        .wasAnotherImplantPlaced as YesNoOptions

    const productTypeInitialValue: ProductTypeOptions = complaint
        .generalComplaint.productType as ProductTypeOptions

    const batchNumInitialValue: string = complaint.generalComplaint
        .batchOrLotNumber as string

    const productTypeMap = {
        IMPLANT: t('pt_option_implant'),
        ABUTMENT: t('pt_option_abutment'),
        SCREW: t('pt_option_screw'),
        CLINICAL_INSTRUMENT: t('pt_option_clinical_instrument'),
        LAB_INSTRUMENT_LAB_SCREW: t('pt_option_lab_instrument_lab_screw'),
    }

    const handleProductTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedProductType = event.target.value as ProductTypeOptions;
      
        // Update the form state with both productType and selectedProduct.productId.
        const productNumberMap: Record<ProductTypeOptions, string> = {
          '': 'XUN001', // Default value for empty string case
          IMPLANT: 'XUN001',
          ABUTMENT: 'XUN002',
          SCREW: 'XUN003',
          CLINICAL_INSTRUMENT: 'XUN004',
          LAB_INSTRUMENT_LAB_SCREW: 'XUN006',
        };
      
        setFormData((prevData) => ({
          ...prevData,
          productType: selectedProductType,
          selectedProduct: {
            ...prevData.selectedProduct!,
            productId: productNumberMap[selectedProductType],
          },
        }));
    };

    const handleBatchNumberOnBlur = () => {

        const cancelTokenSource = axios.CancelToken.source()
        const batchNum = data.batchOrLotNumber

        const validateBatchNum = async (batchNum: string) => {
            try {
                setIsValidatingBatchNum(true)

                let response = await http.get<MaterialResponse>(
                    `/api/Product/Validate?material=${data.selectedProduct?.productId}&batch=${batchNum}&customerNumber=${customerNumber}&countryCode=${countryCode}`,
                    {cancelToken: cancelTokenSource.token})

                if (
                    response &&
                    response.data &&
                    response.data.materialList &&
                    response.data.materialList.length > 0
                ) {
                    if (
                        response.data.materialList[0].batchReturnCode === '300'
                    ) {
                        setIsBatchNumValid(true)
                        setShowBatchNumCheck(true)
                    } else {
                        setIsBatchNumValid(false)
                        setShowBatchNumCheck(false)
                    }
                }

                setIsValidatingBatchNum(false)
            } catch (e) {
            }
        }

        const resetBatchNumState = () => {
            setIsBatchNumValid(true)
            setShowBatchNumCheck(false)
            setIsValidatingBatchNum(false)
        }

        if (batchNum) {
            if (batchNum !== batchNumInitialValue) {
                validateBatchNum(batchNum)
            } else {
                resetBatchNumState()
            }
        } else {
            resetBatchNumState()
        }

        return () => {
            cancelTokenSource.cancel()
        }
    }

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        let isFormValid: boolean = validateFields(data)
        if (isFormValid) {
            if (data.isBatchOrLotNumberKnown === 'YES') {
                isFormValid = isBatchNumValid
            }
        }

        // Stop processing if the form has invalid fields
        if (
            !isFormValid ||
            (data.wasOpenedOrDropped === 'YES'
                && orgIdentifier === "Nobel"
                && data.productType === 'IMPLANT')
        ) {
            postNotification('error', data.wasOpenedOrDropped === 'YES' && data.productType === 'IMPLANT'
                ? t('error_mess_opened_or_dropped')
                : t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        console.log('Dispatching generalComplaint:', {
            ...data,
            productId: data.selectedProduct?.productId || '',
            // ...other fields
        });

        // Package up the form data and update app state
        /*const updatedSelectedProduct = {
            // Make sure all fields are explicitly set
            ...data.selectedProduct,
            productId: data.selectedProduct?.productId ?? '', // Ensure productId is a string
            salesOrg: data.selectedProduct?.salesOrg ?? '', 
            action: data.selectedProduct?.action ?? '', 
            productName: data.selectedProduct?.productName ?? '', 
            validFrom: data.selectedProduct?.validFrom ?? '', 
            shortDescription: data.selectedProduct?.shortDescription ?? '', 
            materialPrice: data.selectedProduct?.materialPrice ?? '', // Ensure materialPrice is a string
            materialPriceCurrency: data.selectedProduct?.materialPriceCurrency ?? '', // Ensure materialPriceCurrency is a string
            categories: data.selectedProduct?.categories ?? null, // Ensure categories is either string[] or null
            isDiscontinuedOrUnknown: data.selectedProduct?.isDiscontinuedOrUnknown ?? false, // Ensure isDiscontinuedOrUnknown is a boolean
            isFirstPartyManufactured: data.selectedProduct?.isFirstPartyManufactured ?? false, // Ensure isFirstPartyManufactured is a boolean
        }*/

        const generalComplaint: GeneralComplaintState = {
            ...data,
            /*selectedProduct: updatedSelectedProduct, // explicitly include the updated nested object*/
            productId: data.selectedProduct?.productId as ProductIdOptions,
            batchOrLotNumber:
                data.isBatchOrLotNumberKnown === 'YES'
                    ? data.batchOrLotNumber
                    : '',
            wasPatientInvolved:
                data.wasProductPlaced === 'NO'
                    ? data.wasPatientInvolved
                    : 'YES',
            wasOpenedOrDropped:
                data.productType === 'IMPLANT' ? data.wasOpenedOrDropped : '',
            wasAnotherImplantPlaced:
                data.productType === 'IMPLANT' &&
                data.wasProductPlaced === 'YES'
                    ? data.wasAnotherImplantPlaced
                    : '',
        }

        // Clear out future fields in the case of changed selections, i.e., the
        // user made selections on this screen, continued to other screens, and
        // then returned to this screen and changed their initial selections

        // Clear out Treatment Date fields that are relevant to another implant placed
        if (
            anotherImplantInitialValue === 'YES' &&
            data.wasAnotherImplantPlaced === 'NO' &&
            !complaint.metadata.complaintWithPatientAndTreatmentData
        ) {
            dispatch(resetTreatmentDates())
        }

        // Clear out Treatment Dates if the user returns to the product type screen
        // and changes the product type
        if (productTypeInitialValue !== data.productType && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(resetTreatmentDates())
        }

        // Clear out complaint type and clinical conditions data if the user returns to
        // the product type screen and changes the product type
        if (productTypeInitialValue !== data.productType) {
            dispatch(resetComplaintType())
            dispatch(resetClinicalCondition())
        }

        if (data.wasAnotherImplantPlaced === 'YES' && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(setTreatmentDates({
                ...complaint.treatmentDates,
                wasProductRemoved: "NO",
                dateProductWasRemoved: null,
                wasThereASecondSurgery: "NO",
                dateOfSecondSurgery: null,
            }))
        }

        // return
        dispatch(setGeneralComplaint(generalComplaint))

        // Proceed to next screen
        navigateForward()
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography variant='h5' sx={{mb: 2}}>
                    {t('pt_selected_product')}
                </Typography>
            </Grid>

            <Stack className={classes.table}
                   sx={{width: '100%'}}
                   direction={{xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row'}}
                   gap={1}
                   justifyContent='space-between'>
                <Box>
                    <Typography
                        variant='subtitle1'
                        className={classes.tableHeading}
                    >
                        {t('material_number')}
                    </Typography>
                    <Typography variant='body1'>
                        {data.selectedProduct?.productId}
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        variant='subtitle1'
                        className={classes.tableHeading}
                    >
                        {t('description')}
                    </Typography>
                    <Typography variant='body1'>
                        {selectedProduct?.shortDescription}
                    </Typography>
                </Box>
                {selectedProduct?.productType && <Box>
                    <Typography
                        variant='subtitle1'
                        className={classes.tableHeading}
                    >
                        {t('product_type')}
                    </Typography>
                    <Typography variant='body1'>
                        {productTypeMap[selectedProduct.productType]}
                    </Typography>
                </Box>}
            </Stack>

            <Grid item xs={12} sx={{mt: 2}}>
                <form onSubmit={handleFormSubmit}>
                    <Grid item xs={12} sx={{mt: 1}}>
                        <FormControl>
                            <FormLabel>
                                {t('pt_is_batch_or_lot_number_known')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={data.isBatchOrLotNumberKnown}
                                onChange={handleChangeEvent(
                                    'isBatchOrLotNumberKnown'
                                )}
                            >
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio/>}
                                    label={t('option_yes') as string}
                                />
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio/>}
                                    label={t('option_no') as string}
                                />
                            </RadioGroup>
                            <FormHelperText
                                error={!!errors.isBatchOrLotNumberKnown}
                            >
                                {errors.isBatchOrLotNumberKnown}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    {data.isBatchOrLotNumberKnown === 'YES' && (
                        <Grid item xs={12} sx={{mt: 0}}>
                            <FormControl sx={{mb: 2}}>
                                <FormLabel sx={{mb: 1}}>
                                    {t('pt_enter_batch_or_lot_number')}:
                                </FormLabel>
                                <TextField
                                    id='batch-lot-number'
                                    error={
                                        typeof errors.batchOrLotNumber !==
                                        undefined || !isBatchNumValid
                                    }
                                    size='small'
                                    // helperText={errors.batchOrLotNumber}
                                    helperText={
                                        !isBatchNumValid ||
                                        errors.batchOrLotNumber
                                            ? t(
                                                'pt_valid_batch_or_lot_number_is_required'
                                            )
                                            : null
                                    }
                                    value={data.batchOrLotNumber}
                                    onChange={handleChangeEvent('batchOrLotNumber')}
                                    inputProps={{maxLength: 10}}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                {isValidatingBatchNum && (
                                                    <CircularProgress size='1rem'/>
                                                )}
                                                {showBatchNumCheck && !isValidatingBatchNum &&
                                                    isBatchNumValid &&
                                                    data.batchOrLotNumber && (
                                                        <CheckIcon
                                                            sx={{
                                                                color: '#666)',
                                                            }}
                                                        />
                                                    )}
                                            </InputAdornment>
                                        ),
                                    }}
                                    onBlur={handleBatchNumberOnBlur}
                                />
                            </FormControl>
                        </Grid>
                    )}

                    {!complaint.metadata.complaintWithPatientAndTreatmentData &&
                        <Grid item xs={12} sx={{mt: 1}}>
                            <FormControl>
                                <FormLabel>
                                    {t('pt_was_the_product_placed')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={data.wasProductPlaced}
                                    onChange={handleChangeEvent('wasProductPlaced')}
                                >
                                    <FormControlLabel
                                        value='YES'
                                        control={<Radio/>}
                                        label={t('option_yes') as string}
                                    />
                                    <FormControlLabel
                                        value='NO'
                                        control={<Radio/>}
                                        label={t('option_no') as string}
                                    />
                                </RadioGroup>
                                <FormHelperText error={!!errors.wasProductPlaced}>
                                    {errors.wasProductPlaced}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    }

                    {data.wasProductPlaced === 'NO' && (
                        <Grid item xs={12} sx={{mt: 1}}>
                            <FormControl>
                                <FormLabel>
                                    {t('pt_was_patient_involved')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={data.wasPatientInvolved}
                                    onChange={handleChangeEvent(
                                        'wasPatientInvolved'
                                    )}
                                >
                                    <FormControlLabel
                                        value='YES'
                                        control={<Radio/>}
                                        label={t('option_yes') as string}
                                    />
                                    <FormControlLabel
                                        value='NO'
                                        control={<Radio/>}
                                        label={t('option_no') as string}
                                    />
                                </RadioGroup>
                                <FormHelperText
                                    error={!!errors.wasPatientInvolved}
                                >
                                    {errors.wasPatientInvolved}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{mt: 1}}>
                        <FormControl>
                            <FormLabel>
                                {t('pt_is_replacement_needed')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={data.isReplacementNeeded}
                                onChange={handleChangeEvent(
                                    'isReplacementNeeded'
                                )}
                            >
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio/>}
                                    label={t('option_yes') as string}
                                />
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio/>}
                                    label={t('option_no') as string}
                                />
                            </RadioGroup>
                            <FormHelperText
                                error={!!errors.isReplacementNeeded}
                            >
                                {errors.isReplacementNeeded}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    {!complaint.generalComplaint.selectedProduct?.productType && <Grid item xs={12} sx={{mt: 1}}>
                        <FormControl>
                            <FormLabel id='product-type-radio-buttons'>
                                {t('pt_select_product_type')}:
                            </FormLabel>
                            <RadioGroup
                                name='radio-buttons-group'
                                value={data.productType}
                                onChange={handleProductTypeChange}
                            >
                                <FormControlLabel
                                    value='IMPLANT'
                                    control={<Radio/>}
                                    label={t('pt_option_implant')}
                                />
                                <FormControlLabel
                                    value='ABUTMENT'
                                    control={<Radio/>}
                                    label={t('pt_option_abutment')}
                                />
                                <FormControlLabel
                                    value='SCREW'
                                    control={<Radio/>}
                                    label={t('pt_option_screw')}
                                />
                                <FormControlLabel
                                    value='CLINICAL_INSTRUMENT'
                                    control={<Radio/>}
                                    label={t('pt_option_clinical_instrument')}
                                />
                                <FormControlLabel
                                    value='LAB_INSTRUMENT_LAB_SCREW'
                                    control={<Radio/>}
                                    label={t('pt_option_lab_instrument_lab_screw')}
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.productType}>
                                {errors.productType}
                            </FormHelperText>
                        </FormControl>
                    </Grid>}
                    {data.productType === 'IMPLANT' && (['us', 'ca', 'hk'].includes(countryCode)) && (
                        <Grid item xs={12} sx={{mt: 1}}>
                            <FormControl>
                                <FormLabel>
                                    {t('pt_implant_opened_or_dropped')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={data.wasOpenedOrDropped}
                                    onChange={handleChangeEvent(
                                        'wasOpenedOrDropped'
                                    )}
                                >
                                    <FormControlLabel
                                        value='YES'
                                        control={<Radio/>}
                                        label={t('option_yes') as string}
                                    />
                                    <FormControlLabel
                                        value='NO'
                                        control={<Radio/>}
                                        label={t('option_no') as string}
                                    />
                                </RadioGroup>
                                <FormHelperText
                                    error={!!errors.wasOpenedOrDropped}
                                >
                                    {errors.wasOpenedOrDropped}
                                </FormHelperText>
                            </FormControl>
                            {
                                orgIdentifier === "Nobel"
                                    ? <IRSAlert wasOpenedOrDropped={data.wasOpenedOrDropped as YesNoOptions}/>
                                    : null
                            }

                        </Grid>
                    )}


                    {data.productType === 'IMPLANT' && (!complaint.metadata.complaintWithPatientAndTreatmentData ||
                        complaint.generalComplaint.wasAnotherImplantPlaced === '') && data.wasProductPlaced === 'YES' && (
                            <Grid item xs={12} sx={{mt: 1}}>
                                <FormControl>
                                    <FormLabel>
                                        {t('pt_another_implant_placed')}
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={data.wasAnotherImplantPlaced}
                                        onChange={handleChangeEvent(
                                            'wasAnotherImplantPlaced'
                                        )}
                                    >
                                        <FormControlLabel
                                            value='YES'
                                            control={<Radio/>}
                                            label={t('option_yes') as string}
                                        />
                                        <FormControlLabel
                                            value='NO'
                                            control={<Radio/>}
                                            label={t('option_no') as string}
                                        />
                                    </RadioGroup>
                                    <FormHelperText
                                        error={!!errors.wasAnotherImplantPlaced}
                                    >
                                        {errors.wasAnotherImplantPlaced}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                        )}

                    <Grid
                        item
                        container
                        direction='row'
                        justifyContent='flex-end'
                        className={classes.buttons}
                        sx={{pt: 4}}
                    >
                        <ControlContainer>
                            <Button
                                disabled={
                                    isValidatingBatchNum &&
                                    data.isBatchOrLotNumberKnown === 'YES'
                                }
                                variant='contained'
                                color='primary'
                                type='submit'
                            >
                                {t('continue')}
                            </Button>

                            <PreviousButton before={() => dispatch(setGeneralComplaint(data))}/>

                            <CancelButton/>
                        </ControlContainer>
                    </Grid>
                </form>
            </Grid>
        </Grid>
    )
}
export default ProductType
